import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the ConfigurationComponentTypeMasterContext
const ConfigurationComponentTypeMasterContext = createContext();

// Custom hook to use the ConfigurationComponentTypeMasterContext
export const useConfigurationComponentType = () =>
  useContext(ConfigurationComponentTypeMasterContext);

// Provider component to wrap the app
export const ConfigurationComponentTypeMasterProvider = ({ children }) => {
  const [component_types, setComponentType] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/payroll/component-types"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchComponentTypes = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setComponentType(response.data?.data || {}); // Access `data.data` for actual component_types master
      const { data, current_page, last_page, per_page, total } = response.data;

      setComponentType(data.data || []); // Update leave types list

      setPagination({
        currentPage: response.data.data.current_page,
        totalPages: response.data.data.last_page,
        totalItems: response.data.data.total,
        perPage: response.data.data.per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching component types master:", error);
      setError(
        error.response?.data?.message || "Failed to fetch component types.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch component_types master from the API on load
  useEffect(() => {
    fetchComponentTypes();
  }, [API_URL, token]);

  // Add a new component_type
  const addComponentType = async (newComponentType) => {
    try {
      const response = await axios.post(API_URL, newComponentType, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setComponentType((prev) => [...prev, response.data?.data]);
      setComponentType((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding component type:", error);
    }
  };

  // Update an existing component_type
  const updateComponentType = async (updatedComponentType) => {
    try {
      const response = await axios.put(
        `${API_URL}/${updatedComponentType.id}`,
        updatedComponentType,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setComponentType((prev) =>
        prev.map((component_type) =>
          component_type.id === updatedComponentType.id
            ? { ...component_type, ...response.data?.data }
            : component_type,
        ),
      );
    } catch (error) {
      console.error("Error updating component type master:", error);
    }
  };

  // Delete a component_type
  const deleteComponentType = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setComponentType((prev) =>
        prev.filter((component_type) => component_type.id !== id),
      );
    } catch (error) {
      console.error("Error deleting component_type:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      component_types,
      addComponentType,
      updateComponentType,
      deleteComponentType,
      fetchComponentTypes,
    }),
    [component_types, isLoading], // Recalculate the value only when `component_types` changes
  );

  return (
    <ConfigurationComponentTypeMasterContext.Provider value={value}>
      {children}
    </ConfigurationComponentTypeMasterContext.Provider>
  );
};
